import { useState } from "react";
import { categories, joinSteps, site } from "@/mocks/chigua";

const FORM_URL = "https://readdy.ai/api/form/daf80b3ep053ijub1kng";

type FormState = { status: "idle" | "ok" | "err"; msg: string };

export default function Join() {
  const [state, setState] = useState<FormState>({ status: "idle", msg: "" });
  const [char, setChar] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const reset = (form: HTMLFormElement) => {
    form.reset();
    setChar(0);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = String(fd.get("website_alt") || "").trim();
    if (honeypot) {
      reset(form);
      setState({ status: "ok", msg: "收到啦！组委会会在 3 个工作日内审核回执。" });
      return;
    }
    fd.delete("website_alt");

    setSubmitting(true);
    setState({ status: "idle", msg: "" });
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = text ? JSON.parse(text) : null;
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta ?? {}) as Record<string, unknown>;
      const serverMsg =
        (meta?.message as string) ||
        (parsed?.message as string) ||
        (meta?.detail as string) ||
        text ||
        "";
      const code = parsed?.code as string;

      if (res.ok && code === "OK") {
        reset(form);
        setState({ status: "ok", msg: "收到啦！组委会会在 3 个工作日内完成审核并回执。" });
      } else {
        const errText = typeof serverMsg === "string" ? serverMsg : "提交失败，请稍后再试。";
        setState({ status: "err", msg: errText });
      }
    } catch {
      setState({ status: "err", msg: "网络开小差了，请稍后再试一次。" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="join" className="scroll-mt-24 bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="relative overflow-hidden rounded-[2rem] bg-secondary-100/60 ring-1 ring-background-200">
          <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-secondary-100 blur-2xl" />
          <div className="pointer-events-none absolute -bottom-20 left-10 h-64 w-64 rounded-full bg-accent-100/70 blur-2xl" />

          <div className="relative grid grid-cols-1 gap-10 p-6 md:p-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14">
            <div>
              <p className="num-tab mb-3 text-xs tracking-[0.3em] text-primary-600">SUBMIT · 提交你的瓜</p>
              <h2 className="font-heading text-3xl font-bold leading-snug tracking-wide text-foreground-950 md:text-4xl">
                手里有颗好瓜？
                <span className="block text-primary-600">那就把它投进赛场</span>
              </h2>
              <p className="mt-5 max-w-md text-sm leading-7 text-foreground-700">
                无论是娱乐名场面、体育争议还是消费热点，只要够有话题、够有后劲，都欢迎提名参赛。
                组委会将择优补入海选池，与万千瓜友一起见证年度瓜王诞生。
              </p>

              <ol className="mt-8 space-y-4">
                {joinSteps.map((s) => (
                  <li key={s.step} className="flex items-start gap-4">
                    <span className="num-tab text-2xl text-primary-600">{s.step}</span>
                    <div>
                      <h3 className="font-semibold text-foreground-950">{s.title}</h3>
                      <p className="mt-0.5 text-sm leading-6 text-foreground-700">{s.desc}</p>
                    </div>
                  </li>
                ))}
              </ol>

              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 rounded-2xl bg-background-50/70 p-4 ring-1 ring-background-200">
                <a href={`mailto:${site.email}`} className="inline-flex items-center gap-2 text-sm text-foreground-700">
                  <i className="ri-mail-line text-primary-600" />
                  {site.email}
                </a>
                <span className="inline-flex items-center gap-2 text-sm text-foreground-700">
                  <i className="ri-chat-3-line text-primary-600" />
                  公众号：{site.wechat}
                </span>
              </div>
            </div>

            {/* form */}
            <div className="rounded-3xl bg-background-50 p-6 ring-1 ring-background-200 md:p-8">
              <h3 className="font-heading text-xl font-bold text-foreground-950">推荐你的候选瓜料</h3>
              <p className="mt-1 text-xs text-foreground-600">带 * 为必填项，我们会在 3 个工作日内审核回执。</p>

              <form
                id="gua-report-form"
                data-readdy-form="吃瓜大赛瓜友爆料与联系表单"
                onSubmit={handleSubmit}
                noValidate
                className="mt-5 flex flex-col gap-4"
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <label className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium text-foreground-800">你的昵称 *</span>
                    <input
                      name="nickname"
                      required
                      maxLength={20}
                      placeholder="怎么称呼你"
                      className="rounded-lg border-0 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-950 ring-1 ring-background-200 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    />
                  </label>
                  <label className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium text-foreground-800">邮箱 *</span>
                    <input
                      name="email"
                      type="email"
                      required
                      placeholder="用来接收审核回执"
                      className="rounded-lg border-0 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-950 ring-1 ring-background-200 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    />
                  </label>
                </div>

                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium text-foreground-800">想推荐的候选主题 *</span>
                  <select
                    name="topic"
                    required
                    defaultValue=""
                    className="rounded-lg border-0 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-950 ring-1 ring-background-200 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  >
                    <option value="" disabled>
                      请选择所属赛区
                    </option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.name}>
                        {c.name} · {c.name}
                      </option>
                    ))}
                    <option value="其他">其他 / 我说不准</option>
                  </select>
                </label>

                <label className="flex flex-col gap-1.5">
                  <span className="flex items-center justify-between text-sm font-medium text-foreground-800">
                    具体瓜料与线索 *
                    <span className="text-xs font-normal text-foreground-400">{char}/500</span>
                  </span>
                  <textarea
                    name="detail"
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="描述事件经过、相关线索或讨论角度，线索越具体越容易通过审核"
                    onChange={(e) => setChar(e.target.value.length)}
                    className="resize-none rounded-lg border-0 bg-background-100 px-3.5 py-2.5 text-sm leading-6 text-foreground-950 ring-1 ring-background-200 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </label>

                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="hf-guest"
                />

                {state.status !== "idle" && (
                  <div
                    role="status"
                    className={`rounded-lg px-4 py-3 text-sm leading-6 ${
                      state.status === "ok"
                        ? "bg-accent-100 text-accent-900"
                        : "bg-primary-100 text-primary-900"
                    }`}
                  >
                    {state.msg}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? (
                    <i className="ri-loader-4-line animate-spin" />
                  ) : (
                    <i className="ri-send-plane-fill" />
                  )}
                  {submitting ? "提交中…" : "提交我的瓜料"}
                </button>
                <p className="text-center text-xs leading-5 text-foreground-500">
                  提交即代表你同意组委会依据公开信息对内容进行审核与整理，请勿填写不实或涉及隐私的爆料。
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}